import {
  CashdroStartOperationResponse,
  CashdroUnitsAlertsResponse,
  CashdroLevelsResponse,
} from './../interfaces/cashdro-specific-responses-interface';
import {
  BCCashdroExecutingOperationsResponse,
  BCCashdroStartOperationsResponse,
} from './../interfaces/bc-cashdro-specific-response-interface';
import {
  BCCashdropResponseInterface,
  CashdroErrorMessageResponse,
} from './../interfaces/cashdro-api-response-interface';
import { get } from 'lodash';
import { CashdroProxyServiceInterface } from './../interfaces/cashdro-proxy-service-interface';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { Terminal } from '@payment-terminals/interfaces/terminal';
import { BCCashdroResponse, CashdroResponse } from '@pos/interfaces/cashdro-api-response-interface';
import { Error, Result } from '@pos/interfaces/cashdro-api-response-interface';
import CashdroErrorHelper from '@pos/helpers/cashdro-error-helper';

import axios, { AxiosInstance } from 'axios';
import {
  CashdroExecutingOperationsResponse,
  CashdroOperationStatusResponse,
} from '@pos/interfaces/cashdro-specific-responses-interface';
import { BCCashdroOperationsStatusResponse } from '@pos/interfaces/bc-cashdro-specific-response-interface';

export default class CashdroProxyService implements CashdroProxyServiceInterface {
  API_ENDPOINT = 'Cashdro3WS';
  WEB_ENDPOINT: 'Cashdro3Web';

  protected $bpcApiRouteHelper: BPCApiRouteHelper;
  protected $errorHelper: CashdroErrorHelper;
  protected $axios: AxiosInstance;

  constructor() {
    this.$bpcApiRouteHelper = new BPCApiRouteHelper();
    this.$errorHelper = new CashdroErrorHelper();
    this.$axios = axios.create({});
  }

  async getTerminalUnitsAlerts(terminal: Terminal): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.getTerminalUnitsAlertsPath(terminal);
    try {
      const res = await this.$axios.get<CashdroResponse<CashdroUnitsAlertsResponse>>(endpoint);
      console.log('Raw getTerminalUnitsAlerts:', res);
      console.log('Raw getTerminalUnitsAlerts data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }

      /* Success Response */
      if (code === 1) {
        if (response.operation?.alerts) {
          // Note: if alerts exist, we return an error.
          return new BCCashdroResponse(
            false,
            response,
            this.$errorHelper.cashdroUnitTermilansWithAlersError(),
          );
        }
        // if no alerts we return the respone.
        return new BCCashdroResponse(true, response, null);
      }

      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async getTerminalLevels(terminal: Terminal): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.getTerminalLevelsPath(terminal);
    try {
      const res = await this.$axios.get<CashdroResponse<CashdroLevelsResponse>>(endpoint);
      console.log('Raw getTerminalLevels:', res);
      console.log('Raw getTerminalLevels data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }
      if (code === 1) {
        /* NOTE: Check if pices has alerts */
        if (response.operation.pieces.length) {
          return new BCCashdroResponse(true, response, null);
          // const alert = response.operation.pieces.find(
          //   level => level.State !== '-1' && parseInt(level.Destination) !== 0,
          // );
          // if (alert) {
          //   /* Error Response */
          //   return new BCCashdroResponse(
          //     false,
          //     response,
          //     this.$errorHelper.cashdroUnitTermilansWithAlersError(),
          //   );
          // }
        } else {
          /* NOTE: if no pieces, cahsdro is empty  */
          /* Error Response */
          return new BCCashdroResponse(false, response, this.$errorHelper.cashdroIsEmptyOfCash());
        }

        /* Success Response */
        return new BCCashdroResponse(true, response, null);
      }

      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async importOperation(
    terminal: Terminal,
    operationId: number | string,
  ): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.importOperationPath(terminal, operationId);
    try {
      const res = await this.$axios.get<CashdroResponse<CashdroErrorMessageResponse>>(endpoint);
      console.log('Raw importOperation:', res);
      console.log('Raw importOperation data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }
      /* Operation is not in execution */
      if (code === -2) {
        return new BCCashdroResponse(
          false,
          res,
          this.$errorHelper.cashdroOperationIsNotInExecutionError(),
        );
      }
      /* Invalid paramenters */
      if (code === -3) {
        return new BCCashdroResponse(
          false,
          res,
          this.$errorHelper.cashdroOperationIsNotInExecutionError(),
        );
      }
      /* Success Response */
      if (code === 1) {
        return new BCCashdroResponse(true, response, null);
      }
      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async cancelOperation(
    terminal: Terminal,
    operationId,
  ): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.getCancelOperationPath(terminal, operationId);
    try {
      const res = await this.$axios.get<CashdroResponse<CashdroErrorMessageResponse>>(endpoint);
      console.log('Raw cancelOperation:', res);
      console.log('Raw cancelOperation data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }
      /* Operation is not in execution */
      if (code === -2) {
        return new BCCashdroResponse(
          false,
          res,
          this.$errorHelper.cashdroOperationIsNotInExecutionError(),
        );
      }
      /* Invalid paramenters */
      if (code === -99) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroInvalidParametersError());
      }
      /* Cashdro service is not running"*/
      if (code === -999) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroServiceStoppedError());
      }
      /* Success Response */
      if (code === 1) {
        return new BCCashdroResponse(true, response, null);
      }
      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async finalizeOperation(
    terminal: Terminal,
    operationId,
  ): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.getFinalizeOperationPath(terminal, operationId);
    try {
      const res = await this.$axios.get<CashdroResponse<CashdroErrorMessageResponse>>(endpoint);
      console.log('Raw finalizeOperation:', res);
      console.log('Raw finalizeOperation data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }
      /* Operation is not in execution */
      if (code === -2) {
        return new BCCashdroResponse(
          false,
          res,
          this.$errorHelper.cashdroOperationIsNotInExecutionError(),
        );
      }
      /* Invalid paramenters */
      if (code === -99) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroInvalidParametersError());
      }
      /* Cashdro service is not running"*/
      if (code === -999) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroServiceStoppedError());
      }
      /* Success Response */
      if (code === 1) {
        return new BCCashdroResponse(true, response, null);
      }
      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async executeOperation(
    terminal: Terminal,
    operationId,
  ): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.executeOperationPath(terminal, operationId);
    try {
      const res = await this.$axios.get<CashdroResponse<CashdroErrorMessageResponse>>(endpoint);
      console.log('Raw executeOperation:', res);
      console.log('Raw executeOperation data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }
      /* Operation not found */
      if (code === -2) {
        return new BCCashdroResponse(
          false,
          res,
          this.$errorHelper.cashdroOperationDoesNotExistException(),
        );
      }
      /* Invalid paramenters */
      if (code === -3) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroInvalidParametersError());
      }
      /* Success Response */
      if (code === 1) {
        return new BCCashdroResponse(true, response, null);
      }
      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async startOperation(
    terminal: Terminal,
    amount: number,
    posId: number,
    contactId: number,
  ): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.startOperationPath(terminal, amount, posId, contactId);

    try {
      const res = await this.$axios.get<CashdroResponse<CashdroStartOperationResponse>>(endpoint);
      console.log('Raw startOperation:', res);
      console.log('Raw startOperation data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      // Authentication Error
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }

      // CashDro busy with another transaction.
      if (code === -2) {
        return new BCCashdroResponse(
          false,
          res,
          this.$errorHelper.cashdroBusyWithOtherOperationError(),
        );
      }

      // Incorrect transaction amount
      if (code === -3) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroIncorrectAmountError());
      }

      // User does not have permission
      if (code === -4) {
        return new BCCashdroResponse(
          false,
          res,
          this.$errorHelper.cashdroUserDoesNotHavePermissionError(),
        );
      }

      // Invalid parameters
      if (code === -99) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroInvalidParametersError());
      }

      // System busy: loading or updating terminal
      if (code === -1900) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroSystemBusyError());
      }

      // Can't start operation. Check system status on Diagnosis screen
      if (code === -998) {
        return new BCCashdroResponse(
          false,
          res,
          this.$errorHelper.cashdroCanNotStartOperationError(),
        );
      }

      // Cashdro service is not running
      if (code === -999) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroServiceStoppedError());
      }

      /* Success Response */
      if (code === 1) {
        const responseData: BCCashdroStartOperationsResponse = {
          code: code,
          operationId: response.operation?.operationId,
        };
        return new BCCashdroResponse(true, responseData, null);
      }
      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async getExecutingTerminalOperations(
    terminal: Terminal,
  ): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.getExecutingTerminalOperationsPath(terminal);
    try {
      const res = await this.$axios.get<CashdroResponse<CashdroExecutingOperationsResponse>>(
        endpoint,
      );
      console.log('Raw getExecutingTerminalOperations:', res);
      console.log('Raw getExecutingTerminalOperations data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }

      /* Success Response */
      if (code === 1) {
        const responseData: BCCashdroExecutingOperationsResponse = {
          code: code,
          operationId: response.operation?.OperationId,
          type: response.operation?.type,
        };

        /* If operation Id is retured, is an error for us, becasue the is a operation in execution */
        const operationId = response.operation?.OperationId;
        const isOkey = operationId && operationId !== -1 ? false : true;
        const error = !isOkey ? this.$errorHelper.cashdroOperationInExecutionError() : null;
        return new BCCashdroResponse(isOkey, responseData, error);
      }
      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async getTerminalStatus(terminal: Terminal): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.getTerminalStatusPath(terminal);
    try {
      const res = await this.$axios.get<CashdroResponse>(endpoint);
      console.log('Raw getTerminalStatus:', res);
      console.log('Raw getTerminalStatus data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }

      /* Success Response */
      if (code === 1) {
        return new BCCashdroResponse(true, response, null);
      }

      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  async getOperationStatus(
    terminal: Terminal,
    operationId,
  ): Promise<BCCashdropResponseInterface<unknown>> {
    const endpoint = this.getOperationStatusPath(terminal, operationId);
    try {
      const res = await this.$axios.get<CashdroResponse<CashdroOperationStatusResponse>>(endpoint);
      console.log('Raw getOperationStatus:', res);
      console.log('Raw getOperationStatus data:', res.data);

      const code = res.data.code;
      const response = res.data.response;
      const error = res.data.response.errorMessage;

      /* Authentication Error */
      if (code === -1) {
        return new BCCashdroResponse(false, res, this.$errorHelper.cashdroAuthenticationError());
      }

      /* Success Response */
      if (code === 1) {
        let errorToReturn = null;

        /* Check operation errors */

        if (response.operation.withError !== 'false' || response.operation.messages.length > 0) {
          errorToReturn = this.$errorHelper.cashdroOperationWithError();
        }

        if (parseInt(response.operation.amountchangenotavailable) !== 0) {
          errorToReturn = this.$errorHelper.cashdroOperationWithAmountChangeNotAvailableError();
        }
        // operation pending to execute (acknowledge)
        if (response.operation.state === 'I') {
          errorToReturn = this.$errorHelper.cashdroOperationDoesNotExistException();
        }

        /*  NOTE: return a better-typed object*/
        const bCResponse: BCCashdroOperationsStatusResponse = {
          code: code,
          operation: {
            operationId: parseInt(response.operation.operationid),
            state: response.operation.state,
            payInProgress: parseInt(response.operation.payInProgress),
            payOutProgress: parseInt(response.operation.payOutProgress),
            total: parseInt(response.operation.total),
            totalin: parseInt(response.operation.totalin),
            totalout: parseInt(response.operation.totalout),
            amountchangenotavailable: parseInt(response.operation.amountchangenotavailable),
            messages: response.operation.messages,
            withError: response.operation.withError === 'false' ? false : true,
            posUser: response.operation.posuser,
            posId: response.operation.posid,
            finalStatus: response.operation.finalStatus,
          },
        };

        return new BCCashdroResponse(!errorToReturn ? true : false, bCResponse, errorToReturn);
      }

      /* Not controlled generic error */
      return new BCCashdroResponse(false, error, this.$errorHelper.genericError());
    } catch (error) {
      return new BCCashdroResponse(false, error, this.$errorHelper.cashdroConnectionError());
    }
  }

  importOperationPath(terminal: Terminal, operationId: any): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'setOperationImported';
    const operationQueryParam = 'operation=' + operation;
    const operationIdQueryParam = 'operationId=' + operationId;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath +
      '?' +
      operationQueryParam +
      '&' +
      operationIdQueryParam +
      '&' +
      usernameQueryParam +
      '&' +
      passwordQueryParam;
    return endpoint;
  }

  getBasePath(terminal: Terminal, type: string): string {
    const ip = terminal.ip;
    // webservice or html
    const route = type === 'api' ? this.API_ENDPOINT : this.WEB_ENDPOINT;
    const basePath = 'https://' + ip + '/' + route + '/' + 'index3.php';
    return basePath;
  }

  getTerminalStatusPath(terminal: Terminal): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'doTest';
    const operationQueryParam = 'operation=' + operation;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath + '?' + operationQueryParam + '&' + usernameQueryParam + '&' + passwordQueryParam;
    return endpoint;
  }

  getOperationStatusPath(terminal: Terminal, operationId): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'askOperationV2';
    const operationQueryParam = 'operation=' + operation;
    const operationIdQueryParam = 'operationId=' + operationId;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath +
      '?' +
      operationQueryParam +
      '&' +
      operationIdQueryParam +
      '&' +
      usernameQueryParam +
      '&' +
      passwordQueryParam;
    return endpoint;
  }

  getExecutingTerminalOperationsPath(terminal: Terminal): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'askOperationExecuting';
    const operationQueryParam = 'operation=' + operation;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath + '?' + operationQueryParam + '&' + usernameQueryParam + '&' + passwordQueryParam;
    return endpoint;
  }

  startOperationPath(terminal: Terminal, amount: number, posId: number, contactId: number): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'startOperation';
    const operationQueryParam = 'operation=' + operation;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const typeQueryParam = 'type=4';
    const posIdQueryParam = 'posid=' + posId;
    const posUserQueryParam = 'posuser=' + contactId;
    const amountQueryParam = 'parameters={"amount":"' + amount + '"}';
    const endpoint =
      basePath +
      '?' +
      operationQueryParam +
      '&' +
      usernameQueryParam +
      '&' +
      passwordQueryParam +
      '&' +
      typeQueryParam +
      '&' +
      posIdQueryParam +
      '&' +
      posUserQueryParam +
      '&' +
      amountQueryParam;
    return endpoint;
  }

  getCancelOperationPath(terminal: Terminal, operationId): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'finishOperation';
    const operationQueryParam = 'operation=' + operation;
    const typeQueryParam = 'type=2';
    const operationIdQueryParam = 'operationId=' + operationId;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath +
      '?' +
      operationQueryParam +
      '&' +
      operationIdQueryParam +
      '&' +
      usernameQueryParam +
      '&' +
      passwordQueryParam +
      '&' +
      typeQueryParam;
    return endpoint;
  }

  getFinalizeOperationPath(terminal: Terminal, operationId): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'finishOperation';
    const operationQueryParam = 'operation=' + operation;
    const typeQueryParam = 'type=1';
    const operationIdQueryParam = 'operationId=' + operationId;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath +
      '?' +
      operationQueryParam +
      '&' +
      operationIdQueryParam +
      '&' +
      usernameQueryParam +
      '&' +
      passwordQueryParam +
      '&' +
      typeQueryParam;
    return endpoint;
  }

  executeOperationPath(terminal: Terminal, operationId: number): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'acknowledgeOperationId';
    const operationQueryParam = 'operation=' + operation;
    const operationIdQueryParam = 'operationId=' + operationId;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath +
      '?' +
      operationQueryParam +
      '&' +
      operationIdQueryParam +
      '&' +
      usernameQueryParam +
      '&' +
      passwordQueryParam;
    return endpoint;
  }

  getTerminalUnitsAlertsPath(terminal: Terminal): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'getAlerts';
    const operationQueryParam = 'operation=' + operation;
    // TODO: check if credentials are needed
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath + '?' + operationQueryParam + '&' + usernameQueryParam + '&' + passwordQueryParam;
    return endpoint;
  }

  getTerminalLevelsPath(terminal: Terminal): string {
    const username = terminal.username;
    const password = terminal.password;
    const basePath = this.getBasePath(terminal, 'api');
    const operation = 'getPiecesCurrency';
    const operationQueryParam = 'operation=' + operation;
    const currencyId = 'EUR';
    const currencyIdQueryParam = 'currencyId=' + currencyId;
    const levels = '1';
    const levelsQueryParam = 'includeLevels=' + levels;
    const usernameQueryParam = 'name=' + username;
    const passwordQueryParam = 'password=' + password;
    const endpoint =
      basePath +
      '?' +
      operationQueryParam +
      '&' +
      currencyIdQueryParam +
      '&' +
      levelsQueryParam +
      '&' +
      usernameQueryParam +
      '&' +
      passwordQueryParam;
    return endpoint;
  }
}
